import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRiskClassificationNomenclature } from '../../hooks/useRiskClassificationNomenclature';
import { useRiskRationaleOptions } from '../../hooks/useRiskRationaleOptions';
import { useNotifications } from '../NotificationSystem';
import {
  RISK_CLASSIFICATION_FREQUENCY_BUCKETS,
  RISK_SCORE_MIN,
  RISK_SCORE_MAX,
  validateScoreRanges
} from '../../utils/riskClassificationConstants';
import './RiskClassificationNomenclature.css';

/**
 * Risk Classification Nomenclature (structural risk — ORDIN 1761).
 * Platform Settings → Risk Configuration → Nomenclature.
 * Map score ranges (2–7) to criticality levels with colors and default frequency.
 */
const RiskClassificationNomenclature = ({ organizationId, userId }) => {
  const { t } = useTranslation('riskClassification');
  const { notify } = useNotifications();
  const {
    nomenclature,
    levels,
    loading,
    error,
    save,
    seedDefault,
    refresh
  } = useRiskClassificationNomenclature(organizationId || null, {
    seedIfEmpty: !!organizationId,
    userId: userId || null
  });

  const [editingLevels, setEditingLevels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const {
    items: rationaleOptions,
    loading: rationaleLoading,
    add: addRationale,
    update: updateRationale,
    remove: removeRationale
  } = useRiskRationaleOptions(organizationId || null, userId || null);
  const [newRationaleLabel, setNewRationaleLabel] = useState('');
  const [editingRationaleId, setEditingRationaleId] = useState(null);
  const [editingRationaleLabel, setEditingRationaleLabel] = useState('');

  const levelsSignature = levels?.length
    ? levels.map(l => `${l.id}:${l.scoreMin}-${l.scoreMax}`).join('|')
    : '';
  useEffect(() => {
    if (!loading) {
      if (levels && levels.length > 0) {
        setEditingLevels(levels.map(l => ({ ...l })));
      } else {
        setEditingLevels([]);
      }
    }
  }, [loading, levelsSignature]);

  const handleAddLevel = () => {
    const newId = `level-${Date.now()}`;
    const lastOrder = editingLevels.length > 0
      ? Math.max(...editingLevels.map(l => l.displayOrder ?? 0))
      : 0;
    setEditingLevels(prev => [...prev, {
      id: newId,
      label: '',
      color: '#6b7280',
      scoreMin: RISK_SCORE_MIN,
      scoreMax: RISK_SCORE_MAX,
      defaultFrequencyCode: 'WEEKLY',
      displayOrder: lastOrder + 1
    }]);
    setValidationError(null);
  };

  const handleRemoveLevel = (index) => {
    setEditingLevels(prev => prev.filter((_, i) => i !== index));
    setValidationError(null);
  };

  const handleLevelChange = (index, field, value) => {
    setEditingLevels(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === 'scoreMin' || field === 'scoreMax') {
        next[index].scoreMin = Number(next[index].scoreMin);
        next[index].scoreMax = Number(next[index].scoreMax);
      }
      if (field === 'displayOrder') next[index].displayOrder = Number(value) || 0;
      return next;
    });
    setValidationError(null);
  };

  const handleSave = async () => {
    setValidationError(null);
    const validation = validateScoreRanges(editingLevels);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }
    if (!userId) {
      notify.error(t('saveFailed'));
      return;
    }
    try {
      setSaving(true);
      await save(editingLevels, userId);
      notify.success(t('saved'));
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddRationale = async () => {
    const label = (newRationaleLabel || '').trim();
    if (!label || !userId) return;
    try {
      setSaving(true);
      await addRationale(label);
      setNewRationaleLabel('');
      notify.success(t('saved'));
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditRationale = (opt) => {
    setEditingRationaleId(opt.id);
    setEditingRationaleLabel(opt.label);
  };

  const handleSaveEditRationale = async () => {
    if (editingRationaleId == null || !userId) return;
    const label = (editingRationaleLabel || '').trim();
    if (!label) return;
    try {
      setSaving(true);
      await updateRationale(editingRationaleId, label);
      setEditingRationaleId(null);
      setEditingRationaleLabel('');
      notify.success(t('saved'));
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRationale = async (optionId) => {
    if (!userId) return;
    try {
      setSaving(true);
      await removeRationale(optionId);
      notify.success(t('saved'));
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleLoadDefault = async () => {
    if (!userId) return;
    try {
      setSaving(true);
      await seedDefault();
      refresh();
      notify.success(t('saved'));
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // No org from user profile — do not use structure fallback; avoid wrong-doc permission errors
  if (organizationId == null || organizationId === '') {
    return (
      <div className="risk-classification-nomenclature">
        <div className="rcn-error">
          <p className="rcn-error-hint">
            Your user account must have an organization assigned (field <strong>organizationId</strong> in the Firestore <strong>users</strong> collection). Contact an administrator to set it.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="risk-classification-nomenclature">
        <div className="rcn-loading">
          <div className="spinner" />
          <p>{t('nomenclatureTitle')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isPermissionError = error.includes('permission') || error.includes('insufficient');
    return (
      <div className="risk-classification-nomenclature">
        <div className="rcn-error">
          <p>{error}</p>
          {isPermissionError && (
            <p className="rcn-error-hint">
              The app only uses your user profile&apos;s organization (no structure fallback). Ensure Firestore rules are deployed: <code>firebase deploy --only firestore:rules</code>
            </p>
          )}
          <button type="button" className="btn btn-secondary" onClick={refresh}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="risk-classification-nomenclature">
      <div className="rcn-header">
        <div>
          <h2>{t('nomenclatureTitle')}</h2>
          <p className="rcn-description">{t('nomenclatureDescription')}</p>
        </div>
        <div className="rcn-actions">
          <button type="button" className="btn btn-secondary" onClick={handleLoadDefault} disabled={saving}>
            {t('loadDefault')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '...' : t('saveNomenclature')}
          </button>
        </div>
      </div>

      {validationError && (
        <div className="rcn-validation-error" role="alert">
          {validationError}
        </div>
      )}

      {editingLevels.length === 0 ? (
        <div className="rcn-empty">
          <p>{t('noNomenclature')}</p>
          <button type="button" className="btn btn-primary" onClick={handleAddLevel}>
            {t('addLevel')}
          </button>
        </div>
      ) : (
        <div className="rcn-table-wrap">
          <table className="rcn-table">
            <thead>
              <tr>
                <th>{t('displayOrder')}</th>
                <th>{t('levelLabel')}</th>
                <th>{t('color')}</th>
                <th>{t('scoreRange')}</th>
                <th>{t('defaultFrequency')}</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {[...editingLevels]
                .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                .map((level) => {
                  const index = editingLevels.findIndex(l => l.id === level.id);
                  return (
                  <tr key={level.id}>
                    <td>
                      <input
                        type="number"
                        min={0}
                        value={level.displayOrder ?? index + 1}
                        onChange={e => handleLevelChange(index, 'displayOrder', e.target.value)}
                        className="rcn-input rcn-order"
                        aria-label={t('displayOrder')}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={level.label ?? ''}
                        onChange={e => handleLevelChange(index, 'label', e.target.value)}
                        className="rcn-input"
                        placeholder={t('levelLabel')}
                        aria-label={t('levelLabel')}
                      />
                    </td>
                    <td>
                      <span className="rcn-color-wrap">
                        <input
                          type="color"
                          value={level.color ?? '#6b7280'}
                          onChange={e => handleLevelChange(index, 'color', e.target.value)}
                          className="rcn-color-picker"
                          aria-label={t('color')}
                        />
                        <input
                          type="text"
                          value={level.color ?? ''}
                          onChange={e => handleLevelChange(index, 'color', e.target.value)}
                          className="rcn-input rcn-hex"
                          placeholder="#000000"
                          aria-label={`${t('color')} hex`}
                        />
                      </span>
                    </td>
                    <td>
                      <span className="rcn-score-range">
                        <input
                          type="number"
                          min={RISK_SCORE_MIN}
                          max={RISK_SCORE_MAX}
                          value={level.scoreMin ?? ''}
                          onChange={e => handleLevelChange(index, 'scoreMin', e.target.value)}
                          className="rcn-input rcn-score"
                          aria-label="Score min"
                        />
                        <span>–</span>
                        <input
                          type="number"
                          min={RISK_SCORE_MIN}
                          max={RISK_SCORE_MAX}
                          value={level.scoreMax ?? ''}
                          onChange={e => handleLevelChange(index, 'scoreMax', e.target.value)}
                          className="rcn-input rcn-score"
                          aria-label="Score max"
                        />
                      </span>
                    </td>
                    <td>
                      <select
                        value={level.defaultFrequencyCode ?? 'WEEKLY'}
                        onChange={e => handleLevelChange(index, 'defaultFrequencyCode', e.target.value)}
                        className="rcn-input rcn-select"
                        aria-label={t('defaultFrequency')}
                      >
                        {RISK_CLASSIFICATION_FREQUENCY_BUCKETS.map(f => (
                          <option key={f.code} value={f.code}>
                            {t(f.labelKey)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost rcn-remove"
                        onClick={() => handleRemoveLevel(index)}
                        aria-label={t('removeLevel')}
                        title={t('removeLevel')}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                  );
                })}
            </tbody>
          </table>
          <button type="button" className="btn btn-secondary rcn-add" onClick={handleAddLevel}>
            {t('addLevel')}
          </button>
        </div>
      )}

      <div className="rcn-rationale-section">
        <h3>{t('rationaleNomenclatureTitle')}</h3>
        <p className="rcn-description">{t('rationaleNomenclatureDescription')}</p>
        {rationaleLoading ? (
          <p className="rcn-muted">{t('loading')}</p>
        ) : (
          <>
            <ul className="rcn-rationale-list">
              {rationaleOptions.map((opt) => (
                <li key={opt.id} className="rcn-rationale-item">
                  {editingRationaleId === opt.id ? (
                    <span className="rcn-rationale-edit-wrap">
                      <input
                        type="text"
                        value={editingRationaleLabel}
                        onChange={(e) => setEditingRationaleLabel(e.target.value)}
                        className="rcn-input"
                        placeholder={t('rationaleOptionLabel')}
                      />
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveEditRationale} disabled={saving}>Save</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEditingRationaleId(null); setEditingRationaleLabel(''); }}>Cancel</button>
                    </span>
                  ) : (
                    <>
                      <span className="rcn-rationale-label">{opt.label}</span>
                      <span className="rcn-rationale-actions">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => handleStartEditRationale(opt)} aria-label="Edit">Edit</button>
                        <button type="button" className="btn btn-ghost btn-sm rcn-remove" onClick={() => handleRemoveRationale(opt.id)} aria-label={t('removeLevel')}>✕</button>
                      </span>
                    </>
                  )}
                </li>
              ))}
            </ul>
            <div className="rcn-rationale-add">
              <input
                type="text"
                value={newRationaleLabel}
                onChange={(e) => setNewRationaleLabel(e.target.value)}
                className="rcn-input"
                placeholder={t('rationaleOptionLabel')}
                onKeyDown={(e) => e.key === 'Enter' && handleAddRationale()}
              />
              <button type="button" className="btn btn-secondary" onClick={handleAddRationale} disabled={saving || !newRationaleLabel.trim()}>
                {t('rationaleAddOption')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RiskClassificationNomenclature;
